// Apply approved AI proposals to the canonical (user-scoped) event store.
// IMPORTANT: this delegates to storage.js so writes land in the SAME
// per-user namespaced key the Calendar UI reads from. Do not write events
// directly here — that previously used an un-scoped key and silently dropped
// every approved change.

import { getAllEvents, createEvent, updateEvent, deleteEvent } from "./storage";
import { getAllTasks, createTask, updateTask, deleteTask } from "./tasks";
import { toMinutes } from "./time";
import { blackReasonsFor } from "./proposalPipeline";

const FIELDS = [
  "title",
  "date",
  "startHour",
  "startMinute",
  "endHour",
  "endMinute",
  "category",
  "priority",
  "description",
  "status",
  "parentEventId",
];

const TASK_KINDS = new Set([
  "task_create",
  "task_edit",
  "task_delete",
  "task_complete",
  "task_skip",
]);
const TASK_FIELDS = ["title", "category", "priority", "targetDate"];

function filteredTaskPatch(patch) {
  const out = {};
  if (!patch) return out;
  for (const k of TASK_FIELDS) {
    if (patch[k] !== undefined && patch[k] !== null) out[k] = patch[k];
  }
  return out;
}

// Approval-gated task actions. Task statuses map: complete -> "success",
// skip -> "failed" (the app's task status enum). Never applied automatically —
// only invoked from ProposalCard on user Approve.
function applyTaskProposal(proposal) {
  if (proposal.kind === "task_create") {
    const n = proposal.new || {};
    if (!n.title) return { ok: false, message: "Task needs a title" };
    createTask({
      title: n.title,
      category: n.category || "general",
      priority: n.priority ?? 3,
      targetDate: n.targetDate || null,
    });
    return { ok: true, message: "Task created" };
  }

  const tasks = getAllTasks();
  const existing = proposal.task_id ? tasks.find((t) => t.id === proposal.task_id) : null;
  if (!existing) return { ok: false, message: "Task not found" };

  if (proposal.kind === "task_delete") {
    deleteTask(existing.id);
    return { ok: true, message: "Task deleted" };
  }
  if (proposal.kind === "task_complete") {
    updateTask(existing.id, { status: "success" });
    return { ok: true, message: "Task completed" };
  }
  if (proposal.kind === "task_skip") {
    updateTask(existing.id, { status: "failed" });
    return { ok: true, message: "Task skipped" };
  }
  // task_edit
  const patch = filteredTaskPatch(proposal.new);
  updateTask(existing.id, patch);
  return { ok: true, message: "Task updated" };
}

function filteredPatch(patch) {
  const out = {};
  if (!patch) return out;
  for (const k of FIELDS) {
    if (patch[k] !== undefined && patch[k] !== null) out[k] = patch[k];
  }
  return out;
}

/**
 * applyProposal(proposal) — returns { ok, message }.
 * Event kinds: create, edit, delete, move, extend, shorten, mark.
 * Task kinds: task_create, task_edit, task_delete, task_complete, task_skip.
 */
export function applyProposal(proposal) {
  if (!proposal || !proposal.kind) {
    return { ok: false, message: "Invalid proposal" };
  }
  // Normalize the parent-event relationship: the AI may name it
  // `partOfAnotherEvent`; the stored field is `parentEventId`.
  if (proposal.new && proposal.new.partOfAnotherEvent !== undefined && proposal.new.parentEventId === undefined) {
    proposal = { ...proposal, new: { ...proposal.new, parentEventId: proposal.new.partOfAnotherEvent } };
  }
  if (TASK_KINDS.has(proposal.kind)) {
    return applyTaskProposal(proposal);
  }
  const events = getAllEvents();
  // AUTHORITATIVE pre-persistence gate: revalidate the FINAL state against the
  // CURRENT store immediately before writing. A BLACK conflict (invalid/unsafe
  // state — self/circular/missing parent, invalid time range) is NEVER persisted,
  // regardless of prior approval or Founder mode.
  if (proposal.kind === "create" || proposal.kind === "edit" || proposal.kind === "move") {
    const blackCodes = blackReasonsFor(
      { new: proposal.new || {}, event_id: proposal.event_id || null },
      events
    );
    if (blackCodes.length) {
      return { ok: false, blocked: true, reasonCodes: blackCodes, message: `Blocked: ${blackCodes.join(", ")}` };
    }
  }
  let existing = proposal.event_id
    ? events.find((e) => e.id === proposal.event_id)
    : null;
  // Defense-in-depth: if the id is missing or stale, resolve the existing event
  // by title + date (+ start time) from `old`/`new`, so an approved edit/delete/
  // move is never silently no-op'd. (The pipeline resolves ids up front; this is
  // a second safety net so nothing the user approved is lost.)
  if (!existing && proposal.kind !== "create") {
    const ref = proposal.old || {};
    const alt = proposal.new || {};
    const title = String(ref.title || alt.title || "").trim().toLowerCase();
    const date = ref.date || alt.date || null;
    if (title || date) {
      let cand = events.filter(
        (e) => (!date || e.date === date) && (!title || String(e.title || "").trim().toLowerCase() === title)
      );
      if (cand.length > 1) {
        const sh = ref.startHour ?? alt.startHour;
        const sm = ref.startMinute ?? alt.startMinute;
        if (sh != null) {
          const narrowed = cand.filter((e) => e.startHour === sh && (e.startMinute || 0) === (sm || 0));
          if (narrowed.length) cand = narrowed;
        }
      }
      if (cand.length === 1) existing = cand[0];
    }
  }

  if (proposal.kind === "create") {
    const n = proposal.new || {};
    if (!n.title || n.date == null) {
      return { ok: false, message: "Create needs title and date" };
    }
    // Idempotency: a create carrying a deterministic sourceEventId that already
    // exists in the store is a duplicate (retry / refresh / re-approval) and is
    // skipped rather than re-created. Reuses the same field bulkCreateEvents uses.
    const sid = n.sourceEventId || null;
    if (sid && events.some((e) => e.sourceEventId === sid)) {
      return { ok: true, message: "Already created", duplicate: true };
    }
    const ev = {
      title: n.title,
      date: n.date,
      startHour: n.startHour ?? 9,
      startMinute: n.startMinute ?? 0,
      endHour: n.endHour ?? 10,
      endMinute: n.endMinute ?? 0,
      description: n.description || "",
      priority: n.priority ?? 3,
      category: n.category || "work",
      status: n.status || "planned",
    };
    if (sid) ev.sourceEventId = sid;
    if (n.parentEventId) ev.parentEventId = n.parentEventId;
    if (toMinutes(ev.endHour, ev.endMinute) <= toMinutes(ev.startHour, ev.startMinute)) {
      return { ok: false, message: "End must be after start" };
    }
    const created = createEvent(ev);
    return { ok: true, message: "Created", id: created.id };
  }

  if (!existing) {
    return { ok: false, message: "Event not found" };
  }

  if (proposal.kind === "delete") {
    deleteEvent(existing.id);
    return { ok: true, message: "Deleted" };
  }

  // edit / move / extend / shorten / mark — all merge `new` onto the existing event.
  const patch = filteredPatch(proposal.new);
  const merged = { ...existing, ...patch };
  if (toMinutes(merged.endHour, merged.endMinute) <= toMinutes(merged.startHour, merged.startMinute)) {
    return { ok: false, message: "End must be after start" };
  }
  updateEvent(existing.id, patch);
  return { ok: true, message: "Updated" };
}
